import { useTranslation } from 'react-i18next';
import { useGetAdStatsBreakdownsQuery } from '@/models/ad-stats';
import { LineChart } from '@mui/x-charts/LineChart';
import { useParams } from 'react-router';
import dayjs from 'dayjs';
import { FC } from 'react';
import { Card } from '@/features/card';

type Props = {
  totalDisplaysBought?: number | null;
  totalViewsBought?: number | null;
  price: number;
};

export const AdStatsChart: FC<Props> = ({
  totalDisplaysBought,
  totalViewsBought,
  price,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const { data: breakdownsData } = useGetAdStatsBreakdownsQuery(
    { id: id ? id : '', type: 'day' },
    { skip: !id }
  );

  if (!totalDisplaysBought && !totalViewsBought) return null;

  const isViews = Boolean(totalViewsBought && !totalDisplaysBought);
  const isDisplays = Boolean(totalDisplaysBought && !totalViewsBought);

  totalDisplaysBought = totalDisplaysBought || 0;
  totalViewsBought = totalViewsBought || 0;

  const start = 0;
  const end = price;

  const numBreakpoints = 8;
  const roundedInterval = end / numBreakpoints;

  const breakpoints = Array.from({ length: numBreakpoints }, (_, i) =>
    parseFloat((start + i * roundedInterval).toFixed(2))
  );

  // Adjust the last value to ensure it matches the exact end value
  breakpoints[breakpoints.length - 1] = end;

  let label;

  if (isViews) {
    label = t('partner.chart.average_cost_per_view');
  }

  if (isDisplays) {
    label = t('partner.chart.average_cost_per_display');
  }

  const getData = () => {
    if (isViews) {
      const viewsBreakdowns =
        breakdownsData?.map((it) => {
          // Prevent division by zero
          if (!it.totalViewsCount || it.totalViewsCount === 0) {
            return 0;
          }
          const result = price / it.totalViewsCount;
          // Check for valid numeric result
          if (!isFinite(result)) {
            return 0;
          }
          return parseFloat(result.toFixed(2));
        }) || [];

      return viewsBreakdowns;
    }

    if (isDisplays) {
      const displaysBreakdowns =
        breakdownsData?.map((it) => {
          // Prevent division by zero
          if (!it.totalDisplaysCount || it.totalDisplaysCount === 0) {
            return 0;
          }
          const result = price / it.totalDisplaysCount;
          // Check for valid numeric result
          if (!isFinite(result)) {
            return 0;
          }
          return parseFloat(result.toFixed(2));
        }) || [];

      return displaysBreakdowns;
    }

    return [];
  };

  return (
    <div className="h-96 mb-4">
      <Card>
        <LineChart
          xAxis={[
            {
              data:
                breakdownsData?.map((it) => dayjs(it.createdAt).toDate()) || [],
              scaleType: 'time',
            },
          ]}
          yAxis={[
            {
              data: breakpoints,
            },
          ]}
          series={[
            {
              data: getData().filter(
                (value) => isFinite(value) && !isNaN(value)
              ),
              label,
              area: true,
              color: 'rgb(151, 0, 35)',
            },
          ]}
          height={350}
        />
      </Card>
    </div>
  );
};
